import { useGetUsersMeQuery, usePostUsersVerificationSendMutation } from '@/services';
import { useState } from 'react';
import { CheckCircle2, Loader2, Mail, X } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { usePostUsersVerificationVerifyMutation } from '@/services/generated';
import { cn } from '@/lib/utils';
import { toast } from './toaster';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './input-otp';

export function VerificationBanner() {
  const { data: userData } = useGetUsersMeQuery();
  const [sendVerification] = usePostUsersVerificationSendMutation();
  const [verifyEmail] = usePostUsersVerificationVerifyMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  if (!userData?.data || userData.data.email_verified || dismissed) {
    return null;
  }

  const handleSendVerification = async () => {
    setIsLoading(true);
    try {
      await sendVerification().unwrap();
      setShowVerifyDialog(true);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter all 6 digits of your verification code.');
      return;
    }

    setIsVerifying(true);
    try {
      await verifyEmail({ body: { code: verificationCode } }).unwrap();
      setShowVerifyDialog(false);
      toast.success('Email verified successfully!');
      // Force refresh user data
      window.location.reload();
    } catch (error) {
      toast.error('Invalid verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="mb-4"
      >
        <div className="bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30 border border-amber-200/80 dark:border-amber-700/50 rounded-none shadow-sm overflow-hidden">
          <div className="p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-700/30 flex items-center justify-center">
                <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>

            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                Email verification required
              </h4>
              <p className="text-sm text-amber-700/80 dark:text-amber-400/80">
                Please verify your email address <span className="font-medium">{userData.data.email}</span> to unlock
                all features.
              </p>
            </div>

            <div className="flex items-center gap-2 self-stretch sm:self-center ml-auto">
              <Button
                onClick={handleSendVerification}
                disabled={isLoading}
                className="bg-amber-500 hover:bg-amber-600 text-white border-none shadow-sm transition-all duration-200 hover:shadow"
                size="sm"
              >
                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Verify Now
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 rounded-full text-amber-700 dark:text-amber-400 hover:bg-amber-200/50 dark:hover:bg-amber-800/50"
                onClick={() => setDismissed(true)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Dismiss</span>
              </Button>
            </div>
          </div>

          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            className="bg-amber-50/80 dark:bg-amber-900/20 border-t border-amber-200/50 dark:border-amber-800/30 px-4 py-3"
          >
            <p className="text-xs text-amber-700/70 dark:text-amber-500/70 italic">
              Don't see the email? Check your spam folder or try again in a few minutes.
            </p>
          </motion.div>
        </div>
      </motion.div>

      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Verify Your Email</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Mail className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">We've sent a verification code to:</p>
              <p className="font-medium">{userData.data.email}</p>
            </div>

            <div className="my-6">
              <InputOTP
                value={verificationCode}
                onChange={setVerificationCode}
                maxLength={6}
                containerClassName="justify-center"
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className={cn(
                        'w-12 h-14 rounded-md text-center text-lg font-semibold',
                        'focus:ring-2 focus:ring-offset-1 focus:ring-amber-500 dark:focus:ring-amber-700',
                        'transition-all duration-200',
                      )}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleVerify}
                disabled={isVerifying || verificationCode.length !== 6}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              >
                {isVerifying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Verify Email
              </Button>

              <div className="text-center">
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSendVerification}
                  disabled={isLoading}
                  className="text-amber-700 dark:text-amber-400"
                >
                  {isLoading ? 'Sending...' : "Didn't receive the code? Resend"}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                The verification code will expire in 24 hours.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AnimatePresence>
  );
}
